import React, { useContext, useEffect, useState, createContext } from "react";
import { authentication as firebaseAuth } from 'lib/firebase';
import { verifyOtp } from "../../api/auth";
import { isValidToken } from "../../utils";
import { getPatient } from "../../api/patient";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

const AuthProvider = ({ children }) => {
  const localToken = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localToken || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("user")) || {}
  );

  async function verifyOtpWithBackend(verifyLoginData) {
    try {
      const authenticatedUser = await verifyOtp(verifyLoginData);
      if (authenticatedUser?.token) {
        setToken(authenticatedUser.token);
        setIsAuthenticated(true); // state was not updating immediately
        return authenticatedUser;
      } else {
        throw new Error("Could not authenticate");
      }
    } catch (err) {
      console.log(err);
    }
  }

  function logout() {
    setToken("");
  }

  /**
   * Resolve the stored session.
   *
   * The session is granted on the LOCAL token check, before the profile is
   * fetched. It used to be granted only after `getPatient()` came back, so on
   * every hard load the guarded tree rendered while that request was still in
   * flight, `PrivateRoute` saw `isAuthenticated === false` and replaced the URL
   * with /login — a logged-in patient was asked to log in again on refresh and
   * on the emailed consultation link. A failed profile fetch is also no longer
   * treated as a failed login: the token is still valid, so the session stands.
   */
  async function handleToken() {
    try {
      if (await isValidToken(token)) {
        localStorage.setItem("token", token);
        setIsAuthenticated(true);
        setLoading(false);

        const patient = await getPatient();
        if (patient) {
          localStorage.setItem("user", JSON.stringify(patient));
          setUser(patient);
        }
        return;
      }

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      setUser({});
    } catch (error) {
      // A malformed token must not leave the app stuck on a blank gate.
      console.log(error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    handleToken();
  }, [token]);

  //   FIREBASE
  //   Phone auth moved to the backend OTP flow, so this is bookkeeping only. It
  //   used to own `loading` — and since it resolves from a local IndexedDB read
  //   in a few ms, it released the gate long before the session had resolved.
  useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      if (user) {
        // setCurrentUser({
        //   user_phone: user._delegate.phoneNumber,
        //   user_uid: user._delegate.auth.currentUser.uid,
        // });
      }
    });
    return unsubscribe;
  }, []);

  const value = {
    isAuthenticated,
    verifyOtpWithBackend,
    logout,
    user,
    setUser,
    // Switching which Patient record the session acts as re-signs the token;
    // setting it here re-runs handleToken, which re-reads the profile and
    // repaints the app as the chosen person.
    setToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
